/* global Promise */
import ApiClient from "../../components/api-client";
import * as _ from "lodash";
import PropertyModel from "./PropertyModel";
import logService from "../view-logs/Service";
import util from "../../components/util";
import config from "../../config";


function createClass(name, rules) {
  let style = document.createElement('style');
  style.type = 'text/css';
  document.getElementsByTagName('head')[0].appendChild(style);
  if(!(style.sheet||{}).insertRule)
    (style.styleSheet || style.sheet).addRule(name, rules);
  else
    style.sheet.insertRule(name+"{"+rules+"}",0);
}

const {instance: Api, asJson: resAsJson} = ApiClient;

function formatNumber(decPlaces = 0) {
  decPlaces = Math.pow(10, decPlaces);
  return function (value) {
    if (!isNaN(value)) {
      const abbrev = ["K", "M", "B", "T"];
      for (let i = abbrev.length - 1; i >= 0; i--) {
        const size = Math.pow(10, (i + 1) * 3);
        if (size <= value) {
          value = Math.round(value * decPlaces / size) / decPlaces;
          if ((value === 1000) && (i < abbrev.length - 1)) {
            value = 1;
            i++;
          }
          value += abbrev[i];
          break;
        }
      }
    }
    return value;
  };
}

const makePanel = () => {
  return {
    stockGraphs: [],
    valueAxes: [
      {id: "line", title: "Temperature", axisColor: "#777", position: "left", offset: 0, labelFunction: formatNumber(0)},
      {id: "column", title: "RPM", offset:0, axisColor: "#777", position: "right",  minimum: 0, maxMultiplier: 2.5, labelFunction: formatNumber(1)},
      {id: "enumLine", title: "State", offset:48, axisColor: "#777", position: "right", minimum: 0, maxMultiplier: 1, integersOnly: true, labelFunction: formatNumber(1)},
      {id: "offlineState", title: "Offline State", axisColor: "FAFAFA", position: "right", minimum: 0, maximum:1, integersOnly: true, labelFunction: formatNumber(1)}
    ],
    stockLegend: {enabled: false}
  };
};

const BaseChartConfig = {
  type: "stock",
  theme: "dark",
  syncDataTimestamps: true,
  addClassNames: true,
  mouseWheelZoomEnabled: true,
  zoomOutOnDataSetChange: true,
  zoomOutOnDataUpdate: true,
  listeners: [],
  valueAxesSettings: {
    axisThickness: 3,
    gridAlpha: 0,
    axisAlpha: 0.87,
    inside: false,
    minMaxMultiplier: 1.05
  },
  panelsSettings: {
    marginTop: 10,
    marginLeft: 60, // inside: false requires that to gain some space
    marginRight: 150
  },

  responsive: {
    enabled: true
  },

  chartScrollbarSettings: {
    updateOnReleaseOnly: false,
    backgroundColor: "#aaa",
    graphFillColor: "#595175",
    graphFillAlpha: 0.8,
    gridColor: "#555",
    gridAlpha: 1,
    selectedBackgroundColor: "#444",
    selectedGraphFillAlpha: 1,
    listeners: []
  },

  chartCursorSettings: {
    valueBalloonsEnabled: false,
    graphBulletSize: 1,
    valueLineBalloonEnabled: false,
    valueLineEnabled: false,
    valueLineAlpha: 0.5,
    listeners: []
  },
  dataSets: [],

  stockEvents: [],

  categoryAxesSettings: {
    maxSeries: 0,
    // groupToPeriods: ["ss", "mm", "10mm", "30mm", "hh", "DD", "WW"],
    minPeriod: "ss",
  },

  legend: {
    enabled: false
  },

  panels: [],

  periodSelector: {
    inputFieldsEnabled: false,
    dateFormat: "MM-DD-YYYY",
    periods: [
      {period: "DD", count: 1, label: "Last 24 Hrs"},
      {period: "DD", count: 3, label: "Last 3 Days"},
      {period: "DD", count: 7, label: "Last 7 Days"},
      {period: "DD", count: 15, label: "Last 15 Days"},
      {period: "MAX", label: "MAX"}
    ]
  }
};

export function ResultSet(keyMap, time, records, boundaries, failureBlobPaths, offlineTimes) {
  this.meta = {keyMap, count: time.length};
  this.time = time;
  this.boundaries = boundaries;
  this.records = records;
  this.failureBlobPaths = failureBlobPaths || [];
  // null for transactional responses (offline inferred from sysTime gaps);
  // present — possibly [] — for polling responses (offline periods supplied by the API).
  this.offlineTimes = offlineTimes;
}
// Note: there is intentionally no client-side merge of ResultSets. Every selection change
// re-fetches all selected fields in one request and replaces the dataset wholesale, so the
// plotted data never depends on the order in which fields were selected (SUB-1489 / DICE Issue 1).
ResultSet.prototype = {
  getRecordAt(index) {
    return Object.assign({}, this.records[index], {time: this.time[index]});
  },
  getSeries(name) {
    return _.map(this.records, this.meta.keyMap[name]);
  },
  hasData(...properties) {
    return !!_.every(properties, ({id}) => !!_.find(this.meta.request.properties, {id}));
  }
};

function doScaling(v, scale) {
  const getTableMapping = v => {
    const mapping = scale.values.split("_")
        .reduce((acc, each) => {
          const spl = each.split(",");
          acc[spl[0]] = spl[1];
          return acc;
        }, {})
    ;
    return Number(mapping[v] || mapping.else);
  };

  switch(scale.mode) {
    case "table":
      return getTableMapping(v);
    case "scale":
    case "temperature":
      // TODO: Convert to C or F if "temperature"
      return v * Number(scale.gain || 1) + Number(scale.offset || 0);
    case "delta":
      break;
    case "divisor":
      return Number(scale.numerator || 1) / v;
    case "bitmask":
      return (Number(v) << Number(scale.shift)) & Number(scale.and);
    default:
      return v;
  }
}

function normalizeGraphData(pModelCfg) {
  console.log("pModelCfg", pModelCfg);
  const properties = new PropertyModel(pModelCfg).getAll(),
      scales = pModelCfg.scales;
  pModelCfg.panels.titles.forEach((title,index)=>{
    const offlinePropModel = util.getOfflineStatePropertyModel((-1*index).toString(), index, title); 
    if (!_.find(properties, {name: offlinePropModel.name})) {
      properties.push(offlinePropModel);
    }
  });
  
  let keyMap = {};
  _.map(properties, "name").forEach((vf, index) => keyMap[vf] = "k" + index);

  return ({time, data, boundaries, failureBlobPaths, offlineTimes, syntheticTimes, clientSyntheticTimes}) => {
    let keys = _.keys(data);
    // Timestamps fabricated by the client for offline-period boundaries (intentional synthetics)
    const offlineBoundaryTimes = new Set(clientSyntheticTimes || []),
        // Timestamps fabricated by the backend for the transactional pre-window boundary carry
        boundaryCarryTimes = new Set(syntheticTimes || []);

    let records = time.map((ts, index) => {
      // Per-record map of series keys whose value was carry-forward synthesized rather than reported,
      // plus the display names of the fields that DID report at this timestamp (for the Row Source label)
      const filled = {};
      const freshFieldNames = [];
      const record = _.reduce(keys, (acc, k) => {
          let v = data[k][index];
          const prop = _.find(properties, {name: k});

          if (prop.type !== "OfflineState") {
            if (!v) {
              //Set last value to next non-null records
              v = index > 0 ? _.findLast(data[k], v => v !== null, index - 1) || 0 : 0;
              filled[keyMap[k]] = true;
            } else {
              freshFieldNames.push(prop.displayName || k);
            }
          }

          v = isNaN(Number(v)) ? v : Number(v);

          if (prop && prop.scale && _.has(scales, prop.scale)) {
            v = doScaling(v, scales[prop.scale]);
          }

          acc[keyMap[k]] = v;
          return acc;
        }, {});
      // Row Source: intentional synthetics keep their labels; otherwise name the fields that were
      // freshly reported at this timestamp — the other columns carry the field's previous value.
      // Names are sorted so the label is identical regardless of response key order / selection order.
      const rowSource = offlineBoundaryTimes.has(ts) ? "offline-boundary"
          : boundaryCarryTimes.has(ts) ? "boundary-carry"
          : _.isEmpty(filled) ? "reported"
          : (freshFieldNames.length ? "reported: " + freshFieldNames.sort().join(", ") : "filled");
      return Object.assign(record, {
        ek: 0,
        ts,
        tsUtc: new Date(ts).toISOString(),
        rowSource,
        filled
      });
    });

    // NEW: Add reverse mapping (ONLY 5 lines added)
    const resultSet = new ResultSet(keyMap, time, records, boundaries, failureBlobPaths, offlineTimes);
    resultSet.meta.reverseKeyMap = _.invert(keyMap); // Create reverse mapping
    return resultSet;
  };
}

function randomColor() {
  const chars = "0123456789abcdef".split("");
  return "#" + (_.times(6).map(() => _.sample(chars)).join(""));
}

export default {
  filterSelection: null,
  timeRangeSelection: {},
  timeRangeIndex: 2,
  customTimeRange: {},
  setTimeRangeFilterSelection(timeSelection, timeRangeIndex, customTimeRange) {
    if(timeSelection) {
      this.timeRangeSelection = timeSelection;
      this.timeRangeIndex = timeRangeIndex;
    }
    if(timeRangeIndex === "custom" && customTimeRange) {
      this.customTimeRange = customTimeRange
    }
  },
  setFilterSelection(selection) {
    this.filterSelection = selection;
  },
  getTimeRangeFilterSelection() {
    return {
      timeRangeSelection: this.timeRangeSelection,
      timeRangeIndex: this.timeRangeIndex,
      customTimeRange: this.customTimeRange,
    };
  },
  getFilterSelection(selection) {
    return this.filterSelection;
  },
  fetchConnectedGraphData(applianceId, applianceType, startTime, endTime, filters = [], excludeProps = [], forceRefresh = false) {
    endTime = endTime || new Date().getTime();
    startTime = startTime || (() => {
      let d = new Date();
      d.setDate(endTime.getDate() - 1);
      return d.getTime();
    })();

    return PropertyModel.forModel(applianceType).then(propModel => {
      const headers = {"Accept-Encoding": "gzip"},
          allProps = new PropertyModel(propModel).getAll();
      let properties = [],
          qs = `startTimeMillis=${startTime}&endTimeMillis=${endTime}&forceRefresh=${forceRefresh}`;

      if (filters && filters.length) {
        filters.forEach(f => {
          properties = properties.concat(_.filter(allProps, f));
        });
      }
      
      // NEW: Auto-include dependent fields (ONLY 10 lines added)
      const originalPropertiesCount = properties.length;
      properties.forEach((prop, index) => {
        if (index < originalPropertiesCount && prop.dependentField) { // Only process original props
          const dependentProp = _.find(allProps, {name: prop.dependentField});
          if (dependentProp && !_.find(properties, {name: prop.dependentField})) {
            properties.push({...dependentProp, autoAdded: true});
          }
        }
      });
      
      if(properties && properties.length && excludeProps && excludeProps.length) {
        excludeProps.forEach(f => {
          _.remove(properties, f);
        });
      }

      if (properties && properties.length) {
        properties = _.uniqBy(properties, "id");
        qs += "&" + (properties.map((p) => {
          if(p.name){
          return "filter=" + p.name;
          } else {
            return "filter=" + p.valueField;
          }
        }).join("&"));

        return Api.get(`/appliances/${applianceId}/connectedgraphdata?${qs}`, {headers})
            .then(resAsJson)
            .then(res=>this.generateDeviceConnectivityStateRecords(res,propModel, startTime, endTime))
            .then(normalizeGraphData(propModel))
            .then(g => {
              g.meta.request = {
                connected: true, startTime, endTime,
                properties
              };
              return g;
            });
      } else {
        return Promise.resolve(null);
      }
    }, errResponse => {
    });
  },

  
  // Defensive numeric sort of the response time axis with all data series reordered in lockstep.
  // Non-monotonic time arrays have been observed on real /connectedgraphdata responses; downstream
  // offline inference and normalization assume chronological order (SUB-1489 / DICE R6).
  sortResponseByTime(res) {
    if (!res || !Array.isArray(res.time) || res.time.length < 2) {
      return res;
    }
    const isSorted = res.time.every((t, i) => i === 0 || res.time[i - 1] <= t);
    if (isSorted) {
      return res;
    }
    const order = res.time.map((t, i) => i).sort((a, b) => res.time[a] - res.time[b]);
    res.time = order.map(i => res.time[i]);
    _.keys(res.data || {}).forEach(k => {
      res.data[k] = order.map(i => res.data[k][i]);
    });
    return res;
  },

  generateDeviceConnectivityStateRecords(res, pModelCfg, startTime, endTime) {
    if(!res.time) res.time = []
    this.sortResponseByTime(res);
    const hasOfflineTimesProperty = Object.prototype.hasOwnProperty.call(res, "offlineTimes");
    // Timestamps inserted below for offline-period boundaries — recorded so exports can label
    // these rows as "offline-boundary" instead of presenting them as reported telemetry.
    res.clientSyntheticTimes = res.clientSyntheticTimes || [];

    const insertTimeWithNulls = (ts, data, times) => {
      if (times.indexOf(ts) !== -1) {
        return;
      }
      const sortedIndex = _.sortedIndex(times, ts);
      times.splice(sortedIndex, 0, ts);
      _.keys(data).forEach(k => {
        data[k].splice(sortedIndex, 0, null);
      });
      if (res.clientSyntheticTimes.indexOf(ts) === -1) {
        res.clientSyntheticTimes.push(ts);
      }
    };

    const normalizeOfflinePeriods = (offlineTimes, requestedStartTime, requestedEndTime) => {
      const requestedStart = requestedStartTime != null ? Number(requestedStartTime) : null;
      const requestedEnd = requestedEndTime != null ? Number(requestedEndTime) : null;
      const periods = (Array.isArray(offlineTimes) ? offlineTimes : [])
        .map(p => ({
          startTime: Number(p && p.startTime),
          endTime: Number(p && p.endTime)
        }))
        .filter(p => Number.isFinite(p.startTime) && Number.isFinite(p.endTime));

      return periods
        .map(p => ({
          startTime: requestedStart != null ? Math.max(p.startTime, requestedStart) : p.startTime,
          endTime: requestedEnd != null ? Math.min(p.endTime, requestedEnd) : p.endTime
        }))
        .filter(p => p.endTime > p.startTime)
        .sort((a, b) => a.startTime - b.startTime);
    };

    const buildOfflineStateSeries = (times, offlinePeriods) => {
      const state = _.fill(Array(times.length), 0);
      if (!offlinePeriods.length) {
        return state;
      }

      let periodIndex = 0;
      for (let i = 0; i < times.length; i++) {
        const ts = times[i];
        while (periodIndex < offlinePeriods.length && ts >= offlinePeriods[periodIndex].endTime) {
          periodIndex++;
        }
        if (periodIndex < offlinePeriods.length) {
          const p = offlinePeriods[periodIndex];
          state[i] = (ts >= p.startTime && ts < p.endTime) ? 1 : 0;
        }
      }
      return state;
    };

    const generateOfflineStateRecords = (propKey, data, times, offlineTimes, offlineTimesProvided, requestedStartTime, requestedEndTime) => {
      // times is already chronologically sorted (with data reordered in lockstep) by sortResponseByTime;
      // sorting only `times` here would silently misalign it from the data series.
      const offlineCheckDeltaInMillis = (config.graphMissingdataIntervalSeconds * 1000) || 360000;

      // Polling devices: if the `offlineTimes` property exists in the response,
      // only display offline periods provided by the API and do not infer from sysTime gaps.
      if (offlineTimesProvided) {
        const offlinePeriods = normalizeOfflinePeriods(offlineTimes, requestedStartTime, requestedEndTime);
        offlinePeriods.forEach(p => {
          insertTimeWithNulls(p.startTime, data, times);
          insertTimeWithNulls(p.endTime, data, times);
        });
        data[propKey] = buildOfflineStateSeries(times, offlinePeriods);
        return;
      }

      // Transactional devices: infer offline periods using sysTime gaps.
      const inferredPeriods = [];
      if (times.length >= 2) {
        for (let i = 0; i < times.length - 1; i++) {
          const delta = times[i + 1] - times[i];
          if (delta > offlineCheckDeltaInMillis) {
            inferredPeriods.push({startTime: times[i], endTime: times[i + 1]});
          }
        }
      }

      // SUB-1482: infer leading/trailing offline only for transactional devices.
      if (times.length > 0) {
        const requestedStart = requestedStartTime != null ? Number(requestedStartTime) : null;
        const requestedEnd = requestedEndTime != null ? Number(requestedEndTime) : null;
        const firstDataTime = times[0];
        const lastDataTime = times[times.length - 1];

        if (requestedStart != null && Number.isFinite(requestedStart) && (firstDataTime - requestedStart) > offlineCheckDeltaInMillis) {
          insertTimeWithNulls(requestedStart, data, times);
          inferredPeriods.push({startTime: requestedStart, endTime: firstDataTime});
        }

        if (requestedEnd != null && Number.isFinite(requestedEnd) && (requestedEnd - lastDataTime) > offlineCheckDeltaInMillis) {
          insertTimeWithNulls(requestedEnd, data, times);
          inferredPeriods.push({startTime: lastDataTime, endTime: requestedEnd});
        }
      }

      inferredPeriods.sort((a, b) => a.startTime - b.startTime);
      data[propKey] = buildOfflineStateSeries(times, inferredPeriods);
    };
    
    const properties = new PropertyModel(pModelCfg).getAll();
    pModelCfg.panels.titles.forEach((title,index)=>{
      const offlinePropModel = util.getOfflineStatePropertyModel((-1*index).toString(), index, title);
      generateOfflineStateRecords(offlinePropModel.name, res.data, res.time, res.offlineTimes, hasOfflineTimesProperty, startTime, endTime);
    });
    return res;
  },

  getTimeZoneId(deviceId) {
    return Api.get(`/timezone/${deviceId}/timezone`,{}).then(resAsJson);
  },

  fetchTimeZones() {
    return Api.get(`/timezone/timezones`,{}).then(resAsJson);
  },

  fetchNonConnectedGraphData(applianceId, applianceType, logType, serial) {
    return PropertyModel.forModel(applianceType, logType).then(propModel => {
      const headers = {"Accept-Encoding": "gzip"},
          qs = `logType=${logType}`;
      let promise;

      if (logType == "Temperature") {
        const data = logService.getLogData();
        if (!data) {
          return Promise.reject();
        }
        promise = Promise.resolve(data);
      } else {
        promise = Api.get(`/appliances/${applianceId}/nonconnectedgraphdata?${qs}`, {headers})
        .then(resAsJson);
      }
      return promise
        .then(normalizeGraphData(propModel))
        .then(g => {
          g.meta.request = {
            connected: false, logType,
            properties: new PropertyModel(propModel).getAll()
          };
          return g;
        });
    }, errResponse => {
    });
  },

  /*
  fetchConnectedGraphData(applianceId, applianceType, startTime, endTime) {
    return PropertyModel.forModel(applianceType).then(propModel => {
      let g = normalizeGraphData(propModel)(MOCK_DATA[applianceId]);
      g.meta.request = {connected: true, startTime, endTime};
      return g;
    });
  },

  fetchNonConnectedGraphData(applianceId, applianceType, logType) {
    return PropertyModel.forModel(applianceType, logType).then(propModel => {
      let g = normalizeGraphData(propModel)(MOCK_DATA[applianceId][logType]);
      g.meta.request = {connected: false, logType};
      return g;
    });
  },
  */

  defaultConnectedChartConfig() {
    return _.cloneDeep(BaseChartConfig);
  },

  defaultGraphPropertyColors() {
    return [
      "#a50026",
      "#d73027",
      "#f46d43",
      "#fdae61",
      "#abd9e9",
      "#74add1",
      "#4575b4",
      "#313695",
      "#543005",
      "#8c510a",
      "#bf812d",
      "#35978f",
      "#01665e",
      "#003c30",
      "#8e0152",
      "#c51b7d",
      "#de77ae",
      "#7fbc41",
      "#4d9221",
      "#276419",
      "#40004b",
      "#762a83",
      "#9970ab",
      "#7f3b08"
    ];
  },

  makeChartConfig({applianceType, mode}, properties, data,
                  prop2Panel = {}, bDataDriven = false) {
    const propModel = PropertyModel.getCached(applianceType, mode);
    if (!(properties && properties.length) || !(data && data.records && data.records.length)) {
      return this.getEmptyChartConfig(propModel);
    }

    let config = _.cloneDeep(BaseChartConfig);

    propModel.panels.sizes.forEach(size => {
      config.panels.push(makePanel());
    });

    const keyMap = data.meta.keyMap,
        keyMapKeys = _.keys(keyMap),
        stateTypes = ["Enum", "Bool"],
        unplottables = ["Text"],
        plottableProps = _.filter(properties, prop => {
          return !_.includes(unplottables, prop.type) &&
              (bDataDriven ? _.includes(keyMapKeys, prop.name) : true)
          ;
        }),
        stateProps = _.filter(plottableProps, prop => _.includes(stateTypes, prop.type)),
        numProps = _.filter(plottableProps, prop => !_.includes(stateTypes, prop.type)),
        fieldMappings = plottableProps.map(prop => ({
          panelIdx: _.has(prop2Panel, keyMap[prop.name]) ?
              Number(prop2Panel[keyMap[prop.name]]) :
              Number(prop.panelIdx || 0),
          color: prop.color,
          event: !!_.find(stateProps, {id: prop.id}),
          fromField: keyMap[prop.name],
          toField: keyMap[prop.name],
          prop
        }));
    const hasRpmType = _.find(numProps,{type:'RPM'}) != null;
    const hasEnumLineType = _.find(numProps,{type:'EnumLine'}) != null;
    if(hasRpmType && hasEnumLineType ){//has at least one RPM
      config.panelsSettings.marginRight = 160;
    }
    else{
      config.panelsSettings.marginRight = 80;
    }

    config.dataSets = [{
      fieldMappings: fieldMappings.concat([
        {panelIdx: 0, hideInLegend: true, color: "#fff", event: false, fromField: "ek", toField: "ek"}
      ]),
      dataProvider: data.records.map(r => Object.assign(r, {ts: new Date(r.ts)})),
      categoryField: "ts"
    }];

    let prop2Graph = {},
        panelCount = 0;
    numProps.forEach(function (prop, index) {
      let axisId = "step",
      suffixUnits = prop.suffixUnits ? ((prop.suffixUnits).replace(/[^\w\s]|_/g, "")).trim() : "";

      switch(prop.type){
        case "RPM":
          axisId =  "column";
          break;
        case "EnumLine":
          axisId = "enumLine";
          break;
        case "OfflineState":
          axisId =  "offlineState";
          break;
        case "Numeric":
          if(suffixUnits.toUpperCase() === "C" || suffixUnits.toUpperCase() === "F") {
            config.panels[0] ? config.panels[0].valueAxes[0] = {id: "line", title: prop.suffixUnits, axisColor: "#777", position: "left", offset: 0, labelFunction: formatNumber(0)} : "";
            config.panels[1] ? config.panels[1].valueAxes[0] = {id: "line", title: prop.suffixUnits, axisColor: "#777", position: "left", offset: 0, labelFunction: formatNumber(0)} : "";
            axisId = "line";
          }
          if(suffixUnits.toUpperCase() === "RPM") {
            config.panels[panelCount] ? config.panels[panelCount].valueAxes[1] = {id: "column", title: prop.suffixUnits, offset:0, axisColor: "#777", position: "right",  minimum: 0, maxMultiplier: 2.5, labelFunction: formatNumber(1)} : "";
            axisId =  "column";
          }
          panelCount = 1;
          break;
        default:
          axisId = "step";
      }
      let panelIdx = _.has(prop2Panel, keyMap[prop.name]) ?
            Number(prop2Panel[keyMap[prop.name]]) : Number(prop.panelIdx || 0),
          panel = config.panels[panelIdx],
          seriesId = "h" + prop.id,
          graphType = "step",
          cfg = {
            id: seriesId,
            bulletSize: 14,
            valueAxis: axisId,
            type: prop.plotAs || graphType,
            dataDateFormat: "YYYY-MM-DDTHH:NN:SS.QQ",
            lineColor: prop.color || randomColor(),
            lineThickness: 1.5,
            useDataSetColors: false,
            valueField: keyMap[prop.name],
            gapField: prop.type !== "OfflineState" ? util.OFFLINE_CONNECTIVITY_STATE_ID: ""
          };
      if (prop.type === "RPM") {
        createClass('.amcharts-graph-' + seriesId + ' .amcharts-graph-stroke',"stroke-dasharray: 5;");
      }
      else if(prop.type === "Numeric"){
        if(prop.suffixUnits === "RPM") {
          createClass('.amcharts-graph-' + seriesId + ' .amcharts-graph-stroke',"stroke-dasharray: 5;");
        }
      }
      else if(prop.type === "EnumLine"){
        createClass('.amcharts-graph-' + seriesId + ' .amcharts-graph-stroke');
         const axis = _.find(panel.valueAxes, {id: 'enumLine'});
         axis.labelFunction = (val)=>{
          return val.toString() || '';//Display No value
         };
         axis.minimum = _.min(axis.minimum, prop.minimum);
         axis.maximum = _.max(axis.maximum, prop.maximum);
         axis.offset = hasRpmType ? 48:0;
      }
      else if( prop.type ===  "OfflineState"){
        //This is hidden axis
        const axis = _.find(panel.valueAxes, {id: 'offlineState'});
        axis.title = "";//Set blank
        axis.axisColor = "#FAFAFA";
        axis.axisAlpha = 1;
        axis.labelsEnabled = false;
        axis.labelFunction = (val)=>{
         return val.toString() || '';//Display No value
        };
        cfg.fillAlphas = 1,
        cfg.fillToAxis = "offlineState";
      }

      if (prop.against) {
        const propCfg = _.find(properties, {id: prop.against}),
            range = data.getSeries(propCfg.name),
            min = _.min(range), max = _.max(range);

        if (min === max) {
          panel.valueAxes = panel.valueAxes.map(va => {
            va.guides = va.guides || [];
            va.guides.push({
              value: min,
              lineAlpha: 0.8,
              lineColor: propCfg.color,
              inside: true,
              label: propCfg.displayName,
              position: "right"
            });
            return va;
          });

          Object.assign(cfg, {
            negativeLineColor: "#67b7dc",
            negativeBase: min
          });
        }
      }

      panel.stockGraphs.push(cfg);
      prop2Graph[prop.id] = seriesId;
    });

    let stockEventSet = [];
    if (stateProps.length) {
      config.dataSets[0].stockEvents = _.reduce(stateProps, (acc, prop) => {
        const key = data.meta.keyMap[prop.name],
            panelIdx = prop.panelIdx || 0,
            graph = prop.targetLine ? prop2Graph[prop.targetLine] || `s${panelIdx}` : `s${panelIdx}`;
        stockEventSet.push({
          panelIdx,
          graph
        });
        if (key) {
          let lastVal;
          data.records.forEach(rec => {
            const disp = prop.displayName,
                vmap = prop.valueMapping;
            if (rec[key] !== undefined && rec[key] !== null && lastVal !== (vmap(rec[key]) || rec[key])) {
              const showOnAxis = !prop.targetLine || prop.targetLine === "x-axis",
                  fromValue = vmap(lastVal) || lastVal,
                  toValue = vmap(rec[key]) || rec[key],
                  description = fromValue && toValue ?
                    `${disp} - ${fromValue} -> ${toValue}` : toValue || undefined;
              acc.push({
                ts: rec.ts,
                date: rec.ts,
                type: showOnAxis ? "pin" : "sign",
                backgroundColor: prop.color || "#85CDE6",
                showOnAxis,
                graph,
                text: "!",
                description
              });
              lastVal = toValue;
            }
          });
        }
        return acc;
      }, []);
    }
    const uniqStockEventSet = _.uniqWith(stockEventSet, _.isEqual);
    if (uniqStockEventSet.length) {
      uniqStockEventSet.forEach((st, ind) => {
        if(_.findIndex(config.panels[st.panelIdx].stockGraphs, {id: st.graph}) == -1) {
          config.panels[st.panelIdx].stockGraphs.push({
            id: st.graph,
            valueAxis: "line",
            type: "step",
            lineColor: "#000",
            lineThickness: 1,
            lineAlpha: 0.2,
            valueField: "ek"
          });
        }
      });
    }
    /*if (data.boundaries) {
      config.panels[0].stockGraphs.push({
        id: "boundary_indicator",
        lineAlpha: 1,
        valueField: "boundary"
      });

      Object.assign(config.chartScrollbarSettings, {
        graph: "boundary_indicator",
        graphType: "column",
        minimum: 0,
        maximum: 1
      });
    }*/
    return config;
  },

  getEmptyChartConfig({panels}) {
    panels = panels || {sizes: [100], titles: [" "]};

    let config = _.cloneDeep(BaseChartConfig);


    panels.sizes.forEach(p => {
      config.panels.push({
        stockGraphs: [{
          id: "dummy",
          title: "",
          type: "step",
          valueField: ""
        }],
        valueAxes: [{
          id: "line", title: " ", axisColor: "#777",
          position: "left", offset: 1, minimum: 0, maximum: 100
        }],
        stockLegend: {enabled: false}
      });
    });

    let ts = new Date(), ts2 = new Date(ts);
    ts2.setDate(ts2.getDate() - 1);
    config.dataSets = [{
      fieldMappings: [{color: "#fff", fromField: "dummy", toField: "dummy", hideInLegend: true}],
      dataProvider: [{dummy: 0, ts}, {dummy: 0, ts: ts2}],
      categoryField: "ts"
    }];

    config.chartCursorSettings.enabled = false;
    config.chartScrollbarSettings.enabled = false;

    return config;
  }
};
